<script lang="ts">
	import type { InputControl } from '$lib/components/atoms/input/types';
	import type { Coordinates } from '$lib/components/map/types';
	import type { ItineraryStore } from '$lib/components/search/itinerary/ItineraryStore';
	import type { WayPoint } from '$lib/components/search/itinerary/types';
	import type { ActiveWaypoint } from '$lib/components/search/types';
	import type { DragOptions } from '@neodrag/svelte';
	import { browser } from '$app/environment';
	import ControlStack from '$lib/components/control-stack/ControlStack.svelte';
	import FavoriteItem from '$lib/components/search/FavoriteItem/FavoriteItem.svelte';
	import { computeMyLocation } from '$lib/components/search/itinerary/userLocation';
	import WaypointSearch from '$lib/components/search/itinerary/WaypointSearch.svelte';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import * as m from '$paraglide/messages';
	import { draggable } from '@neodrag/svelte';
	import { createEventDispatcher } from 'svelte';
	import { flip } from 'svelte/animate';
	import { readable } from 'svelte/store';

	export let proximity: Coordinates;
	export let itineraryStore: ItineraryStore;
	export let active = false;
	let activeWaypoint: WayPoint | undefined = undefined;

	const dispatch = createEventDispatcher<{
		activeWaypointSearch: ActiveWaypoint;
	}>();

	function activate(wp: WayPoint, detail: ActiveWaypoint) {
		active = true;
		activeWaypoint = wp;
		track({
			action: 'itinerary_field',
			payload: {
				action: 'click',
				field: getWaypointTrackingField(wp),
			},
		});

		dispatch('activeWaypointSearch', detail);
	}

	function getWaypointTrackingField(wp: WayPoint) {
		return wp.isArrival ? 'arrival' : wp.index === 0 ? 'departure' : `step${wp.index}`;
	}

	function deactivate(waypoint: WayPoint) {
		if (waypoint.id === activeWaypoint?.id) {
			active = false;
			activeWaypoint = undefined;
		}
	}

	$: itinerary
		= activeWaypoint && browser && $isMobile ? readable([activeWaypoint]) : itineraryStore.itinerary;
	$: itineraryHasMyLocation = itineraryStore.itineraryHasMyLocation;

	let dragging = false;
	let currentDragIndex = 0;
	let itemHeight = 0;
	let relativeOffsetY = 0;
	let lastOffsetY = 0;
	const dragOptions: DragOptions = {
		axis: 'y',
		bounds: 'parent',
		handle: '.handle',
		onDragStart({ rootNode, offsetY }) {
			dragging = true;
			rootNode.classList.add('z-ontop', 'cursor-n-resize', 'shadow-float');
			rootNode.querySelector('.handle')?.classList.add('cursor-n-resize');
			itemHeight = rootNode.clientHeight;
			lastOffsetY = offsetY;
			relativeOffsetY = 0;
			currentDragIndex = [...rootNode.parentNode!.children].indexOf(rootNode);
			track({
				action: 'itinerary_step',
				payload: {
					action: 'drag',
				},
			});
		},
		onDrag({ offsetY }) {
			relativeOffsetY += offsetY - lastOffsetY;
			lastOffsetY = offsetY;
			if (relativeOffsetY > 0.5 * itemHeight) {
				itineraryStore.updateIndex(currentDragIndex, currentDragIndex + 1);
				currentDragIndex += 1;
				relativeOffsetY -= itemHeight;
			} else if (relativeOffsetY < -0.5 * itemHeight) {
				itineraryStore.updateIndex(currentDragIndex, currentDragIndex - 1);
				currentDragIndex -= 1;
				relativeOffsetY += itemHeight;
			}
		},
		onDragEnd({ rootNode }) {
			rootNode.classList.remove('z-ontop', 'cursor-n-resize', 'shadow-float');
			rootNode.querySelector('.handle')?.classList.remove('cursor-n-resize');
			rootNode.style.transform = 'translate3d(0,0,0)';
			dragging = false;
			relativeOffsetY = 0;
		},
		transform() {
			return `translate3d(0,${relativeOffsetY}px,0)`;
		},
	};

	function getControls(waypoint: WayPoint, active: boolean): InputControl[] | undefined {
		if (!active && $itinerary?.length > 2) {
			return [
				{
					icon: 'drag-handle',
					ariaLabel: 'drag',
					class: 'handle cursor-grab',
				},
				{
					icon: 'delete',
					ariaLabel: 'delete',
					click: () => {
						itineraryStore.removeWayPoint(waypoint.index);
						track({
							action: 'itinerary_step',
							payload: {
								action: 'bin',
							},
						});
					},
				},
			];
		}
	}

	function getFlipOptions(waypoint: WayPoint) {
		if (
			dragging
			&& waypoint.index !== currentDragIndex + 1
			&& waypoint.index !== currentDragIndex - 1
		) {
			return { duration: 0 };
		}
		return { duration: 200 };
	}

	function handleSelectedWaypoint(waypoint: WayPoint) {
		if (waypoint.coordinates === 'MY_LOC') {
			// Call the function to start computing the user location
			// this may take some time so we start it as soon as possible
			computeMyLocation();
			track({
				action: 'itinerary_current_location',
				payload: {
					action: 'click',
					from: getWaypointTrackingField(waypoint),
				},
			});
		}

		itineraryStore.updateWaypoint(waypoint);
	}
</script>

<ControlStack class={dragging ? 'bg-white border-rounded-1 rd-2' : 'rd-2'}>
	{#each $itinerary as waypoint (waypoint.id)}
		<div
			animate:flip={getFlipOptions(waypoint)}
			use:draggable={dragOptions}
			class="relative"
			class:z-infront={waypoint.id === activeWaypoint?.id}
		>
			<WaypointSearch
				{waypoint}
				{proximity}
				includeMyLocationOption={!$itineraryHasMyLocation}
				controls={getControls(waypoint, waypoint.id === activeWaypoint?.id)}
				onSelect={handleSelectedWaypoint}
				onActivate={detail => activate(waypoint, detail)}
				onDeactivate={() => {
					deactivate(waypoint);
				}}
				placeholder={waypoint.isArrival
					? m.route__arrival()
					: waypoint.index === 0
					? m.route__departure()
					: m.itinerary__add_step()}
			>
				{#snippet beforeListItems(onmouseover, activeItemIndex, value)}
					<FavoriteItem {onmouseover} {activeItemIndex} {value} />
				{/snippet}
			</WaypointSearch>
		</div>
	{/each}
</ControlStack>
